"use client";

import React from "react";
import { CircleCheck, ReceiptText, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
const pricingLines = [{
  label: "Base Rate (2 Guests)",
  value: "$120",
  suffix: "/night"
}, {
  label: "Weekend Rate (Fri-Sun)",
  value: "$125",
  suffix: "/night"
}, {
  label: "Extra Guest (after 2)",
  value: "$35",
  suffix: "/night"
}];
const benefits = ["No hidden booking fees", "Secure deposit via PayPal", "Pay remaining balance upon arrival", "Sleeps up to 9 guests"];
export default function TransparentStayPricing() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardRef,
    isVisible: isCardVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="174142" id="transparent-pricing" className="bg-background py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col gap-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-sans">
                Clear & Simple Pricing
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl font-sans">
                At Paradise Palms Jamaica, we believe your Caribbean getaway should be stress-free from the very first click. We offer straightforward rates with absolutely no hidden service fees or surprises. What you see is exactly what you pay for your stay.
              </p>
            </div>

            <ul className="space-y-4">
              {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <CircleCheck className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground text-base md:text-lg font-sans">{benefit}</span>
                </li>)}
            </ul>

            <div className="pt-4">
              <Button asChild size="lg" className="rounded-full px-8 shadow-lg shadow-primary/20 bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300">
                <Link to="/ReservationPage">
                  Check Availability
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={cardRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isCardVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {}
            <div className="absolute -inset-4 bg-primary/10 rounded-[2.5rem] blur-3xl -z-10" />
            
            <Card className="border-border/40 shadow-2xl shadow-shadow-color/5 bg-card/80 backdrop-blur-xl overflow-hidden rounded-3xl">
              <div className="bg-muted/40 px-6 py-4 border-b border-border/40 flex items-center gap-3">
                <ReceiptText className="w-5 h-5 text-primary" />
                <span className="font-medium text-sm text-muted-foreground uppercase tracking-widest font-sans">Rate Details</span>
              </div>
              
              <CardHeader className="pt-8 pb-6">
                <CardTitle className="text-2xl md:text-3xl font-sans font-bold text-center text-foreground">
                  Pricing Breakdown
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-6 px-6 md:px-10 pb-10">
                <div className="space-y-4">
                  {pricingLines.map((line, i) => <div key={i} data-index={i} className="flex justify-between items-end gap-4">
                      <span className="text-muted-foreground text-base md:text-lg leading-none font-sans">
                        {line.label}
                      </span>
                      <div className="flex-1 border-b border-dashed border-border/40 mb-1" />
                      <div className="text-right leading-none">
                        <span className="font-bold text-foreground text-lg md:text-xl font-sans">{line.value}</span>
                        <span className="text-muted-foreground text-sm ml-1 font-sans">{line.suffix}</span>
                      </div>
                    </div>)}
                </div>

                <Separator className="my-6 bg-border/40" />

                <div className="flex justify-between items-center pt-2">
                  <span className="text-xl md:text-2xl font-bold text-foreground font-sans">
                    Starting Total
                  </span>
                  <div className="text-right">
                    <span className="text-2xl md:text-3xl font-bold text-primary font-sans">$120</span>
                    <span className="text-muted-foreground text-base ml-1 font-sans">/night</span>
                  </div>
                </div>
                
                <p className="text-center text-xs text-muted-foreground pt-4 font-sans italic">
                  *Rates are subject to change based on season and availability.
                </p>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}